import { Link } from "@tanstack/react-router";

import type { CollectionItem } from "@/lib/collection";
import { Photo } from "@/components/site/photo";
import { Reveal } from "@/components/site/reveal";

export function CollectionCard({
  item,
  delay = 0,
  description,
  sizes,
  eager = false,
  detailed = false,
}: {
  item: CollectionItem;
  delay?: number;
  description: string;
  sizes: string;
  eager?: boolean;
  detailed?: boolean;
}) {
  return (
    <Reveal as="li" delay={delay}>
      <article
        id={item.slug}
        className="group flex h-full flex-col overflow-hidden rounded-3xl border border-border/70 bg-card shadow-[var(--shadow-soft)] transition-all duration-500 hover:-translate-y-2 hover:shadow-[var(--shadow-lift)]"
      >
        <div className="overflow-hidden">
          <Photo
            {...item.photo}
            alt={item.alt}
            eager={eager}
            sizes={sizes}
            className="aspect-square w-full object-cover transition-transform duration-700 group-hover:scale-[1.07]"
          />
        </div>
        <div className="flex flex-1 flex-col p-6">
          <h3 className="font-display text-xl">{item.name}</h3>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{description}</p>

          {detailed && (item.includes.length > 0 || item.craft || item.occasion) && (
            <div className="mt-5 space-y-4 border-t border-border/60 pt-5">
              {item.includes.length > 0 && (
                <div>
                  <h4 className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-gold-ink">
                    What's included
                  </h4>
                  <ul className="mt-2 flex flex-wrap gap-1.5">
                    {item.includes.map((inc) => (
                      <li
                        key={inc}
                        className="rounded-full bg-secondary px-3 py-1 text-xs text-muted-foreground"
                      >
                        {inc}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {item.craft ? (
                <div>
                  <h4 className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-gold-ink">
                    The craft
                  </h4>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {item.craft}
                  </p>
                </div>
              ) : null}
              {item.occasion ? (
                <div>
                  <h4 className="text-[0.68rem] font-semibold uppercase tracking-[0.16em] text-gold-ink">
                    Suits
                  </h4>
                  <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                    {item.occasion}
                  </p>
                </div>
              ) : null}
            </div>
          )}

          <Link
            to="/contact"
            search={{ piece: item.slug }}
            className="mt-auto inline-block pt-5 text-[0.72rem] font-semibold uppercase tracking-[0.16em] text-gold-ink transition-colors hover:text-primary"
          >
            Ask about this piece &rarr;
          </Link>
        </div>
      </article>
    </Reveal>
  );
}
